import React, { useState } from 'react';
import { tokens } from "../../theme";
import {
    Dialog, DialogTitle, DialogContent, DialogActions, Button,
    Box, Typography, useTheme, TextField, Tabs, Tab, CircularProgress,
    IconButton, Tooltip,
} from '@mui/material';
import AutoFixHighIcon from '@mui/icons-material/AutoFixHigh';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import CheckIcon from '@mui/icons-material/Check';

const MANUAL_PROMPT = `You are an expert at analyzing test PDFs and extracting their full structure (MCQ, FRQ, and Short Answer).

Analyze the uploaded test PDF and extract ALL sections and questions.

IMPORTANT: Use COMPACT format to save tokens. Only include fields relevant to each question type.

MCQ: {"n": 1, "t": "mcq", "a": "A"}  (add "nc": 5 only if not 4 choices, "ms": true only if multi-select, "ol": ["A","B","C","D"] only if labels differ from default A/B/C/D — e.g. "ol": ["1","2","3","4"] for numbered options)
Multi-Answer: {"n": 1, "t": "multi_answer", "a": "main answer", "aa": ["answer", "variant1", "variant2"]}
SAQ/DBQ/LEQ with NESTED parts:
{"n": 1, "t": "saq", "mp": 10, "fp": [
  {"pl": "a", "mp": 3, "sp": [{"pl": "i", "mp": 1}, {"pl": "ii", "mp": 2}]},
  {"pl": "b", "mp": 2},
  {"pl": "c", "mp": 2}
]}

IMPORTANT for parts: Use NESTED structure. If a part has sub-parts (like a→i,ii,iii), use "sp" (subparts) array.
Parts can nest to any depth: fp→sp→sp. Only leaf nodes (no sp) are gradable.

Types: mcq, multi_answer, saq (short answer question), dbq (document-based), leq (long essay)
Keys: n=question_number, t=type, a=answer, mp=max_points, fp=parts, sp=subparts, pl=label, aa=accepted_answers, nc=num_choices, ms=multi_select, ol=option_labels

Return: {"sections": [{"name": "Section name", "questions": [...]}]}
Extract ALL questions with proper nesting. Return ONLY valid JSON.`;

/**
 * Expand compact AI JSON format to full format used by our system.
 */
function expandCompactJson(data) {
    if (!data?.sections) return data;

    return {
        sections: data.sections.map((s, idx) => ({
            section_name: s.name || '',
            section_number: idx + 1,
            section_type: inferSectionType(s.questions),
            questions: (s.questions || []).map(q => expandQuestion(q)),
        })),
    };
}

function inferSectionType(questions) {
    if (!questions?.length) return 'mixed';
    const types = new Set(questions.map(q => q.t || q.question_type));
    if (types.size === 1) return types.values().next().value;
    return 'mixed';
}

function expandParts(parts) {
    return (parts || []).map((p, i) => ({
        part_label: p.pl || p.part_label || String.fromCharCode(97 + i),
        part_order: i + 1,
        criteria_name: p.cn || p.criteria_name || '',
        max_points: p.mp || p.max_points || 1,
        rubric_text: p.rt || p.rubric_text || '',
        rubric_template_id: '',
        subparts: expandParts(p.sp || p.subparts || []),
    }));
}

function expandQuestion(q) {
    // Support both compact (n, t, a) and full (question_number, question_type, answer) format
    const type = q.t || q.question_type || 'mcq';
    const num = q.n || q.question_number;
    const answer = q.a || q.answer || '';

    const base = {
        question_number: num,
        question_type: type,
        answer: ['saq', 'dbq', 'leq'].includes(type) ? '' : answer,
        max_points: q.mp || q.max_points || 1,
        accepted_answers: q.aa || q.accepted_answers || null,
        options: [],
        parts: [],
    };

    // MCQ options
    if (type === 'mcq') {
        const customLabels = q.ol || q.option_labels || null;
        const numChoices = customLabels?.length || q.nc || q.num_choices || 4;
        base.options = Array.from({ length: numChoices }, (_, i) => ({
            option_label: customLabels?.[i] || String.fromCharCode(65 + i),
            option_text: '',
            order: i,
        }));
        base.is_multi_select = q.ms || q.is_multi_select || false;
    }

    // Multi-Answer (SA)
    if (type === 'multi_answer') {
        base.accepted_answers = q.aa || q.accepted_answers || (answer ? [answer] : null);
    }

    // FRQ parts (SAQ, DBQ, LEQ)
    if (['saq', 'dbq', 'leq'].includes(type)) {
        base.max_points = q.mp || q.max_points || 1;

        const parts = q.fp || q.frq_parts || q.parts || [];
        base.parts = expandParts(parts);

        // Choice group
        if (q.cg || q.choice_group) {
            base.choice_group = q.cg || q.choice_group;
        }
    }

    return base;
}

const AIAutoFillModal = ({ open, onClose, onApply, testFileUrl, answerKeyUrl, api }) => {
    const theme = useTheme();
    const colors = tokens(theme.palette.mode);
    const [tab, setTab] = useState(1); // 0 = auto, 1 = manual (default)
    const [analyzing, setAnalyzing] = useState(false);
    const [manualJson, setManualJson] = useState('');
    const [jsonError, setJsonError] = useState('');
    const [copied, setCopied] = useState(false);

    // Auto mode — fetch files from S3 URLs and send to our API
    const handleAutoFill = async () => {
        if (!testFileUrl && !answerKeyUrl) return;
        setAnalyzing(true);
        try {
            const formData = new FormData();
            if (testFileUrl) {
                const resp = await fetch(testFileUrl);
                const blob = await resp.blob();
                formData.append('file', blob, 'test.pdf');
            }
            if (answerKeyUrl) {
                const resp = await fetch(answerKeyUrl);
                const blob = await resp.blob();
                formData.append('answerKey', blob, 'answer_key.pdf');
            }

            const res = await api.upload('/tests/analyze-pdf', formData);
            const data = res.data.data;

            if (data?.sections?.length > 0) {
                onApply(data);
                onClose();
            } else {
                setJsonError('AI could not extract any sections from the PDF');
            }
        } catch (err) {
            setJsonError(err.response?.data?.message || 'Failed to analyze PDF');
        } finally {
            setAnalyzing(false);
        }
    };

    const [applying, setApplying] = useState(false);

    // Manual mode — parse pasted JSON with loading state
    const handleManualApply = () => {
        setJsonError('');
        setApplying(true);
        // Defer to next tick so spinner renders before heavy work
        setTimeout(() => {
            try {
                let parsed = JSON.parse(manualJson.trim());
                parsed = expandCompactJson(parsed);

                if (!parsed?.sections?.length) {
                    setJsonError('JSON must have a "sections" array with at least one section');
                    setApplying(false);
                    return;
                }

                onApply(parsed);
                onClose();
            } catch (e) {
                setJsonError(`Invalid JSON: ${e.message}`);
            }
            setApplying(false);
        }, 50);
    };

    const handleCopyPrompt = () => {
        navigator.clipboard.writeText(MANUAL_PROMPT);
        setCopied(true);
        setTimeout(() => setCopied(false), 2000);
    };

    return (
        <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
            <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <AutoFixHighIcon color="info" />
                AI Auto-fill
            </DialogTitle>
            <DialogContent>
                <Tabs value={tab} onChange={(_, v) => { setTab(v); setJsonError(''); }} sx={{ mb: 2 }}>
                    <Tab label="Automatic" />
                    <Tab label="Manual (paste JSON)" />
                </Tabs>

                {tab === 0 ? (
                    <Box>
                        <Typography variant="body2" color="text.secondary" mb={2}>
                            Upload a test PDF and/or answer key above, then click Auto-fill.
                            Our server will send the file(s) to GPT-4o and extract all sections and questions automatically.
                        </Typography>
                        <Box display="flex" gap={2} mb={1}>
                            <Typography variant="body2">
                                Test PDF: {testFileUrl ? <b>Uploaded</b> : <i>not uploaded</i>}
                            </Typography>
                            <Typography variant="body2">
                                Answer Key: {answerKeyUrl ? <b>Uploaded</b> : <i>not uploaded</i>}
                            </Typography>
                        </Box>
                        {jsonError && <Typography color="error" variant="body2" mt={1}>{jsonError}</Typography>}
                    </Box>
                ) : (
                    <Box>
                        <Typography variant="body2" color="text.secondary" mb={1}>
                            Use your own AI tool (ChatGPT, Claude, etc.) to extract questions. Copy the prompt below,
                            upload your test PDF to the AI, then paste the JSON response here.
                        </Typography>

                        {/* Prompt box */}
                        <Box sx={{
                            position: 'relative',
                            bgcolor: theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.04)' : 'rgba(0,0,0,0.03)',
                            borderRadius: 1, p: 2, mb: 2, maxHeight: 200, overflowY: 'auto',
                            fontFamily: 'monospace', fontSize: 12, whiteSpace: 'pre-wrap',
                            border: `1px solid ${colors.grey[700]}`,
                        }}>
                            <Tooltip title={copied ? 'Copied!' : 'Copy prompt'}>
                                <IconButton
                                    size="small"
                                    onClick={handleCopyPrompt}
                                    sx={{ position: 'absolute', top: 8, right: 8 }}
                                >
                                    {copied ? <CheckIcon fontSize="small" color="success" /> : <ContentCopyIcon fontSize="small" />}
                                </IconButton>
                            </Tooltip>
                            {MANUAL_PROMPT}
                        </Box>

                        {/* Paste area */}
                        <TextField
                            fullWidth
                            multiline
                            rows={8}
                            label="Paste AI JSON response here"
                            value={manualJson}
                            onChange={(e) => { setManualJson(e.target.value); setJsonError(''); }}
                            placeholder='{"sections": [{"name": "Section I", "questions": [{"n": 1, "t": "mcq", "a": "B"}, ...]}]}'
                            sx={{ fontFamily: 'monospace' }}
                            error={Boolean(jsonError)}
                            helperText={jsonError}
                        />
                    </Box>
                )}
            </DialogContent>
            <DialogActions>
                <Button onClick={onClose}>Cancel</Button>
                {tab === 0 ? (
                    <Button
                        variant="contained"
                        color="info"
                        startIcon={analyzing ? <CircularProgress size={20} /> : <AutoFixHighIcon />}
                        onClick={handleAutoFill}
                        disabled={analyzing || (!testFileUrl && !answerKeyUrl)}
                    >
                        {analyzing ? 'Analyzing...' : 'Auto-fill'}
                    </Button>
                ) : (
                    <Button
                        variant="contained"
                        color="info"
                        onClick={handleManualApply}
                        disabled={!manualJson.trim() || applying}
                        startIcon={applying ? <CircularProgress size={18} sx={{ color: 'white' }} /> : null}
                    >
                        {applying ? 'Applying...' : 'Apply JSON'}
                    </Button>
                )}
            </DialogActions>
        </Dialog>
    );
};

export { expandCompactJson };
export default AIAutoFillModal;
